import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { renderClose } from "../../common/dialog";
import { createEntityStore } from "../../common/entityStore";
import { propBoolField, propEnumField, propTextField } from "../../common/entityStoreForm";
import { propIf, propLength, propLengthMax, propNotRegex, propRequired, propValidEmail, propValueValidator } from "../../common/entityStoreValidators";
import { CallCenterProjectDto, newUser, UserCallCenterProjectEditDto, UserEditDto, UserListItemDto } from "./types";
import { createListStore, UseListStore } from "../../common/listStore";
import { onlyTmUserTypeIds, tmUserTypeIds, useCallCenters, useLanguages, useTmUserTypes, useTmUserTypesForSupervisor, useUserTypes } from "../../common/enum";
import { minPasswordLength } from "../../common/config";
import { UserTypeId, useAccountStore } from "../../common/account";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { combineLatest, map } from "rxjs";
import { renderListStore } from "../../common/listStoreUi";
import { UserCallCenterProjectEnabledControl } from "./UserCallCenterProjectEnabledControl";
import { keyBy } from "lodash";

export const UserDetailDialog = (props: {
    dialogProps: DialogProps;
    close: () => void;
    user: UserListItemDto | null;
    onUserChange(): void;
    useUserListStore: UseListStore<UserListItemDto>;
    useCallCenterProjectListStore: UseListStore<CallCenterProjectDto>;
}) => {
    const { isAdmin, isTmSupervisor } = useAccountStore(p => ({
        isAdmin: p.data?.accessTokenDecoded.userTypeId === UserTypeId.Admin,
        isTmSupervisor: p.data?.accessTokenDecoded.userTypeId === UserTypeId.TmSupervisor,
    }));
    const [{ useUserStore }] = useState(() => {
        const useUserStore = createEntityStore<UserEditDto>({
            data: newUser(),
            path: "users",
            id: props.user != null ? props.user.id : null,
            getNewId: p => p.id!,
            onSaved() {
                props.onUserChange();
                props.close();
            },
        });

        return { useUserStore };
    });

    const [useUserCallCenterProjectListStore] = useState(() => createListStore<UserCallCenterProjectEditDto>({
        data: combineLatest([
            useUserStore.getState().clientData$.pipe(map(p => ({
                userCallCenterProjects: p.data.userCallCenterProjects,
                callCenterId: p.data.callCenterId,
                isFetching: p.isFetching,
            }))),
            props.useCallCenterProjectListStore.getState().items$,
        ]).pipe(map(([user, callCenterProjects]) => {
            const userCallCenterProjectsDict = keyBy(user.userCallCenterProjects, "callCenterProjectId");
            return {
                items: callCenterProjects.items.filter(p => p.callCenterId === user.callCenterId).map(p => ({
                    callCenterProjectId: p.id,
                    projectName: p.projectName,
                    isEnabled: userCallCenterProjectsDict[p.id] != null ? true : false,
                })),
                isFetching: user.isFetching || callCenterProjects.isFetching,
            };
        })),
        getId: p => p.callCenterProjectId,
        listProps: [
            {
                column: "isEnabled", label: "Enabled", type: "bool", width: 62,
                renderCell: p => <UserCallCenterProjectEnabledControl useUserStore={useUserStore} userCallCenterProjectDto={p.row} />
            },
            { column: "projectName", label: "Project", flex: 1 },
        ],
    }));

    useEffect(() => {
        useUserStore.getState().setPropValidators([
            propRequired("userName"),
            propLength("userName", 4, 50),
            propNotRegex("userName", /^[a-z]/, "First character must be lower case letter."),
            propNotRegex("userName", /^[a-z0-9_]*$/, "Only lower case letters, digits and underscores are allowed."),
            propValueValidator("userName", (p, e) => e.id == null && props.useUserListStore.getState().items.map(p => p.userName).includes(p) && "User name must be unique."),
            propRequired("firstName"),
            propLengthMax("firstName", 50),
            propRequired("lastName"),
            propLengthMax("lastName", 50),
            propRequired("userTypeId"),
            propRequired("email"),
            propLengthMax("email", 100),
            propValidEmail("email"),
            propIf(p => p.id == null, () => propRequired("password")),
            propLength("password", minPasswordLength, 100),
            isAdmin && propIf(p => p.userTypeId != null && onlyTmUserTypeIds.includes(p.userTypeId),
                () => propRequired("callCenterId")),
        ]);
    }, [useUserStore, isAdmin, props.useUserListStore]);

    const { serverData, canHaveCallCenter, isUserCallCenterProjectsEnabled, hasCallCenter } = useUserStore(p => ({
        serverData: p.serverData,
        canHaveCallCenter: getCanHaveCallCenter(p.clientData.userTypeId),
        isUserCallCenterProjectsEnabled: p.clientData.isUserCallCenterProjectsEnabled,
        hasCallCenter: p.clientData.callCenterId != null,
    }));

    return <Dialog {...props.dialogProps} maxWidth={isUserCallCenterProjectsEnabled ? "md" : "xs"}>
        <DialogTitle>{serverData.id == null ? "New user" : `Edit user - ${serverData.firstName} ${serverData.lastName}`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="start">
                <Grid container item columnSpacing={2} xs={isUserCallCenterProjectsEnabled ? 6 : 12}>
                    <Grid item xs={12}>{propTextField(useUserStore)("userName", "User name", { disabled: serverData.id != null })}</Grid>
                    <Grid item xs={6}>{propTextField(useUserStore)("firstName", "First name")}</Grid>
                    <Grid item xs={6}>{propTextField(useUserStore)("lastName", "Last name")}</Grid>
                    <Grid item xs={6}>{propEnumField(useUserStore)("userTypeId", "Type",
                        isAdmin ? useUserTypes : isTmSupervisor ? useTmUserTypesForSupervisor : useTmUserTypes, {
                        filter: isTmSupervisor ? p => p.id !== UserTypeId.TmManager : undefined,
                        onChange(p) {
                            if (!getCanHaveCallCenter(p)) {
                                useUserStore.getState().setClientDataProp("callCenterId", null);
                            }
                        },
                    })}</Grid>
                    <Grid item xs={6}>{propTextField(useUserStore)("password", "Password", { type: "password" })}</Grid>
                    <Grid item xs={8}>{propTextField(useUserStore)("email", "Email")}</Grid>
                    <Grid item xs={4}>{propBoolField(useUserStore)("isActive", "Active")}</Grid>
                    {isAdmin && <Grid item xs={8}>{propEnumField(useUserStore)("callCenterId", "Call center", useCallCenters,
                        { disabled: !canHaveCallCenter, addNone: true })}</Grid>}
                    {isAdmin && <Grid item xs={4}>{propEnumField(useUserStore)("language", "Language", useLanguages)}</Grid>}
                    {hasCallCenter && <Grid item xs={12}>{propBoolField(useUserStore)("isUserCallCenterProjectsEnabled", "Specify enabled call center projects")}</Grid>}
                </Grid>
                {isUserCallCenterProjectsEnabled && <Grid item columnSpacing={2} xs={6}>
                    <Typography variant="h5" color="textPrimary" component="h2">
                        Call center projects
                    </Typography>
                    {renderListStore(useUserCallCenterProjectListStore, {
                        rowHeight: 30,
                    })}
                </Grid>}
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useUserStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </Dialog>;
};

function getCanHaveCallCenter(userTypeId: UserTypeId | null | undefined): boolean {
    return userTypeId != null && tmUserTypeIds.includes(userTypeId);
}
